import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { DatabaseService } from '../../services/DatabaseService';

const PollList = () => {
  // State management
  const [polls, setPolls] = useState([]);         // All user polls
  const [loading, setLoading] = useState(true);   // Loading state
  const [error, setError] = useState('');         // Error messages
  const [searchTerm, setSearchTerm] = useState(''); // Search input value
  const [filteredPolls, setFilteredPolls] = useState([]); // Polls filtered by search
  
  // Get the current authenticated user
  const { currentUser } = useAuth();
  
  // Fetch all polls created by the current user
  useEffect(() => {
    const fetchPolls = async () => {
      try {
        const userPolls = await DatabaseService.getUserPolls(currentUser.uid);
        
        // Format polls to ensure dates and participant counts display correctly
        const formattedPolls = userPolls.map(poll => {
          // Format creation date
          let formattedDate = 'Unknown Date';
          if (poll.createdAt) {
            try {
              // Try to create a valid date from the createdAt value
              const date = typeof poll.createdAt === 'object' && poll.createdAt.toDate 
                ? poll.createdAt.toDate() 
                : new Date(poll.createdAt);
                
              if (!isNaN(date)) {
                formattedDate = date.toLocaleDateString();
              }
            } catch (err) {
              console.error('Error formatting date:', err);
            }
          }
          
          // Get participant count
          let participantCount = 0;
          if (Array.isArray(poll.participantIds)) {
            participantCount = poll.participantIds.length;
          } else if (poll.participantCount) {
            participantCount = poll.participantCount;
          } else if (Array.isArray(poll.participants)) {
            participantCount = poll.participants.length;
          }
          
          return {
            ...poll,
            formattedDate,
            participantCount
          };
        });
        
        setPolls(formattedPolls);
        setFilteredPolls(formattedPolls); // Initially show all polls
      } catch (err) {
        setError('Failed to load polls: ' + err.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchPolls();
  }, [currentUser]);
  
  // Filter polls based on search term
  useEffect(() => {
    if (searchTerm.trim() === '') {
      // If search is empty, show all polls
      setFilteredPolls(polls);
      return;
    }
    
    const term = searchTerm.toLowerCase();
    const filtered = polls.filter(poll => {
      return (
        poll.title.toLowerCase().includes(term) ||
        (poll.description && poll.description.toLowerCase().includes(term)) ||
        (poll.category && poll.category.toLowerCase().includes(term))
      );
    });
    
    setFilteredPolls(filtered);
  }, [searchTerm, polls]);
  
  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      {/* Header with Create Poll button */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Your Polls</h1>
        <Link
          to="/polls/create"
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          Create New Poll
        </Link>
      </div>
      
      {/* Error alert */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4" role="alert">
          <span className="block sm:inline">{error}</span>
        </div>
      )}
      
      {/* Search input */}
      <div className="mb-6">
        <div className="mt-1 relative rounded-md shadow-sm">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg className="h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
            </svg>
          </div>
          <input
            type="text"
            className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-3 py-2 border-gray-300 rounded-md"
            placeholder="Search polls by title, description or category"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>
      
      {/* Loading spinner */}
      {loading ? (
        <div className="flex justify-center items-center py-8">
          <svg className="animate-spin h-8 w-8 text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
      ) : (
        <>
          {/* Empty state when no polls match */}
          {filteredPolls.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">
                {polls.length === 0 ? "You haven't created any polls yet." : "No polls matching your search."}
              </p>
              {polls.length === 0 && (
                <Link
                  to="/polls/create"
                  className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200"
                >
                  Create your first poll
                </Link>
              )}
            </div>
          ) : (
            /* Poll list */
            <div className="bg-white shadow overflow-hidden sm:rounded-md">
              <ul className="divide-y divide-gray-200">
                {filteredPolls.map((poll) => (
                  <li key={poll.id}>
                    <Link to={`/polls/${poll.id}`} className="block hover:bg-gray-50">
                      <div className="px-4 py-4 sm:px-6">
                        <div className="flex items-center justify-between">
                          <div className="truncate">
                            <div className="flex text-sm">
                              <p className="font-medium text-indigo-600 truncate">{poll.title}</p>
                              <p className="ml-1 flex-shrink-0 font-normal text-gray-500">
                                {poll.category && `in ${poll.category}`}
                              </p>
                            </div>
                            <div className="mt-2 flex">
                              {/* Created date */}
                              <div className="flex items-center text-sm text-gray-500">
                                <svg className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                                </svg>
                                <span>Created: {poll.formattedDate}</span>
                              </div>
                              {/* Participant count */}
                              <div className="ml-4 flex items-center text-sm text-gray-500">
                                <svg className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                  <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
                                </svg>
                                <span>
                                  {poll.participantCount} {poll.participantCount === 1 ? 'participant' : 'participants'}
                                </span>
                              </div>
                              {/* Poll status */}
                              <div className="ml-4 flex items-center text-sm text-gray-500">
                                <svg className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                </svg>
                                <span>
                                  {poll.isActive ? 'Active' : 'Closed'}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="ml-5 flex-shrink-0">
                            <svg className="h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                            </svg>
                          </div>
                        </div>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PollList;